/**
 * Engineering Requests API — proactive Operate entry point.
 * Request → Engineering Package → (approval) → Mission. Not a chatbot or ticket system.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, buildApiHeaders } from './api';

const API_BASE = '/api/v1';

export interface EngineeringRequestLifecycleStep {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'done' | 'skipped' | string;
}

export interface EngineeringRequestEvidence {
  id: string;
  request_id: string;
  kind: string;
  name: string;
  mime_type?: string;
  size_bytes?: number;
  text_excerpt?: string;
  uri?: string;
  created_at: string;
}

export interface EngineeringRequestClarification {
  clarification_id: string;
  request_id: string;
  question: string;
  answer?: string;
  status: string;
  timestamp: string;
  missing_evidence?: string;
  blocked_engineering_decision?: string;
  confidence_before?: number;
  expected_confidence_after?: number;
  reasoning?: string;
}

export interface EngineeringPackage {
  id: string;
  schema?: string;
  request_id: string;
  engineering_intent: string;
  request_type?: string;
  understanding: string;
  impact_analysis: string;
  architecture_analysis?: string;
  affected_applications?: string[];
  affected_repositories?: string[];
  affected_services?: string[];
  affected_apis?: string[];
  affected_components?: string[];
  affected_infrastructure?: string[];
  proposed_engineering_plan: string;
  validation_strategy?: string;
  /** Implementation details Engineering Intelligence decided by policy
   * instead of a clarification round — shown so the reviewer sees what was
   * settled and why, never hidden. */
  engineering_defaults?: EngineeringDefault[];
  confidence: number;
  required_approvals?: string[];
  sealed_at: string;
  approved_at?: string | null;
}

export interface EngineeringDefault {
  topic?: string;
  decision: string;
  default: string;
  rationale?: string;
  confidence?: number;
  question?: string;
}

export interface EngineeringRequest {
  request_id: string;
  id?: string;
  project_id: string;
  title: string;
  engineering_intent: string;
  intent_text?: string;
  request_type?: string;
  status: string;
  stage?: string;
  submitted_by?: string;
  confidence: number;
  understanding?: Record<string, unknown>;
  repository_context?: Record<string, unknown>;
  clarification_state?: Record<string, unknown>;
  engineering_package?: EngineeringPackage | null;
  engineering_package_reference?: string;
  mission_reference?: string;
  execution_reference?: string;
  engineering_operation_reference?: string;
  entry_point?: string;
  evidence?: EngineeringRequestEvidence[];
  clarifications?: EngineeringRequestClarification[];
  open_clarification?: EngineeringRequestClarification | null;
  lifecycle?: EngineeringRequestLifecycleStep[];
  status_history?: string[];
  created_at: string;
  updated_at: string;
}

export function requestIdOf(r: EngineeringRequest): string {
  return r.request_id || r.id || '';
}

export function useEngineeringRequests(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'engineering-requests'],
    queryFn: async () => {
      const data = await apiClient<{ requests: EngineeringRequest[] }>(
        `/projects/${projectId}/engineering-requests`,
      );
      return data.requests ?? [];
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
    refetchInterval: 20_000,
  });
}

export function useEngineeringRequest(projectId: string, requestId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'engineering-requests', requestId],
    queryFn: ({ signal }) =>
      apiClient<EngineeringRequest>(`/projects/${projectId}/engineering-requests/${requestId}`, { signal }),
    enabled: Boolean(projectId && requestId),
    staleTime: 5_000,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status) return 8_000;
      if (
        ['understanding', 'collecting_evidence', 'clarification_required', 'submitted'].includes(
          status,
        )
      ) {
        return 5_000;
      }
      return 30_000;
    },
  });
}

export function useSubmitEngineeringRequest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title?: string;
      engineering_intent: string;
      repository_urls?: string[];
      submitted_by?: string;
    }) =>
      apiClient<EngineeringRequest>(`/projects/${projectId}/engineering-requests`, {
        method: 'POST',
        body: JSON.stringify(body),
        timeoutMs: 90_000,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests'] });
    },
  });
}

export function useAdvanceEngineeringRequest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      apiClient<EngineeringRequest>(`/projects/${projectId}/engineering-requests/${requestId}/advance`, {
        method: 'POST',
        timeoutMs: 90_000,
      }),
    onSuccess: (data) => {
      const id = requestIdOf(data);
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests'] });
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests', id] });
    },
  });
}

export function useAnswerEngineeringClarification(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      clarificationId,
      answer,
    }: {
      requestId: string;
      clarificationId: string;
      answer: string;
    }) =>
      apiClient<EngineeringRequest>(
        `/projects/${projectId}/engineering-requests/${requestId}/clarifications/${clarificationId}/answer`,
        {
          method: 'POST',
          body: JSON.stringify({ answer }),
          timeoutMs: 90_000,
        },
      ),
    onSuccess: (data) => {
      const id = requestIdOf(data);
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests'] });
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests', id] });
    },
  });
}

export function useApproveEngineeringPackage(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      apiClient<EngineeringRequest>(`/projects/${projectId}/engineering-requests/${requestId}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
        timeoutMs: 60_000,
      }),
    onSuccess: (data) => {
      const id = requestIdOf(data);
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests'] });
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests', id] });
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'change-requests'] });
    },
  });
}

export function useRejectEngineeringPackage(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      apiClient<EngineeringRequest>(`/projects/${projectId}/engineering-requests/${requestId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      }),
    onSuccess: (data) => {
      const id = requestIdOf(data);
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests'] });
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests', id] });
    },
  });
}

export async function uploadEngineeringRequestEvidence(
  projectId: string,
  requestId: string,
  file: File,
  advance = true,
): Promise<EngineeringRequest> {
  const form = new FormData();
  form.append('file', file);
  const qs = advance ? '?advance=true' : '?advance=false';
  const response = await fetch(
    `${API_BASE}/projects/${projectId}/engineering-requests/${requestId}/evidence${qs}`,
    {
      method: 'POST',
      body: form,
      headers: { ...buildApiHeaders() },
    },
  );
  const wrapped = await response.json();
  if (!response.ok || !wrapped.success) {
    throw new Error(wrapped.error?.message || `Upload failed (${response.status})`);
  }
  return wrapped.data as EngineeringRequest;
}

export async function attachEngineeringRequestRepoURL(
  projectId: string,
  requestId: string,
  repositoryUrl: string,
  advance = true,
): Promise<EngineeringRequest> {
  const form = new FormData();
  form.append('repository_url', repositoryUrl);
  const qs = advance ? '?advance=true' : '?advance=false';
  const response = await fetch(
    `${API_BASE}/projects/${projectId}/engineering-requests/${requestId}/evidence${qs}`,
    {
      method: 'POST',
      body: form,
      headers: { ...buildApiHeaders() },
    },
  );
  const wrapped = await response.json();
  if (!response.ok || !wrapped.success) {
    throw new Error(wrapped.error?.message || `Attach failed (${response.status})`);
  }
  return wrapped.data as EngineeringRequest;
}
